import type { DictationReviewSnapshot } from '@viapeople/components'
import { Button, Modal, Text } from '@viapeople/components'

interface DictationReviewModalProps {
	isOpen: boolean
	snapshots: DictationReviewSnapshot[]
	onClose: () => void
	onConfirm: () => void
}

export function DictationReviewModal({ isOpen, snapshots, onClose, onConfirm }: DictationReviewModalProps) {
	return (
		<Modal isOpen={isOpen} onClose={onClose} title='Review Dictated Changes'>
			<div className='flex flex-col gap-5'>
				{snapshots.length === 0 ? (
					<div className='flex flex-col items-center gap-3 py-8'>
						<span className='h-8 w-8 rounded-full bg-success-100 flex items-center justify-center'>
							<span className='h-3 w-3 rounded-full bg-success-500' />
						</span>
						<Text variant='small' className='text-neutral-600'>
							No dictated changes to review. You&apos;re good to go!
						</Text>
					</div>
				) : (
					<>
						<div className='flex items-center gap-2 px-3 py-2 rounded-md bg-primary-50 border border-primary-100'>
							<span className='h-2 w-2 rounded-full bg-primary-500' />
							<Text variant='xsmall' className='text-primary-700'>
								{snapshots.length} field{snapshots.length > 1 ? 's' : ''} modified via dictation. Please review before
								submitting.
							</Text>
						</div>

						<div className='flex flex-col gap-3 max-h-[45vh] overflow-y-auto pr-1'>
							{snapshots.map((snapshot, index) => (
								<div key={snapshot.fieldId} className='rounded-lg border border-neutral-200 overflow-hidden'>
									<div className='flex items-center gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200'>
										<span className='h-5 w-5 rounded bg-neutral-200 flex items-center justify-center'>
											<Text variant='hint' className='text-neutral-600 font-medium'>
												{index + 1}
											</Text>
										</span>
										<Text variant='xsmall' className='text-neutral-600 font-medium'>
											Field {index + 1}
										</Text>
									</div>

									<div className='grid grid-cols-1 md:grid-cols-2 gap-0'>
										<div className='p-3 flex flex-col gap-1'>
											<Text variant='hint' className='text-neutral-500 font-medium uppercase tracking-wide'>
												Before
											</Text>
											<Text
												variant='xsmall'
												className={
													snapshot.before
														? 'text-neutral-700 whitespace-pre-wrap leading-relaxed'
														: 'text-neutral-400 italic'
												}>
												{snapshot.before || 'Empty'}
											</Text>
										</div>

										<div className='p-3 bg-success-50/50 flex flex-col gap-1 border-t md:border-t-0 md:border-l border-neutral-200'>
											<Text variant='hint' className='text-success-700 font-medium uppercase tracking-wide'>
												After
											</Text>
											<Text
												variant='xsmall'
												className={
													snapshot.after
														? 'text-neutral-800 whitespace-pre-wrap leading-relaxed'
														: 'text-neutral-400 italic'
												}>
												{snapshot.after || 'Empty'}
											</Text>
										</div>
									</div>
								</div>
							))}
						</div>
					</>
				)}

				<div className='flex items-center justify-between pt-3 border-t border-neutral-100'>
					<Button variant='secondary' size='small' onClick={onClose}>
						Go Back
					</Button>
					<Button variant='primary' size='small' onClick={onConfirm}>
						Accept &amp; Submit
					</Button>
				</div>
			</div>
		</Modal>
	)
}
